import { gyroControl } from '../airplane/state.js';

// Mobile detection
export function isMobileDevice() {
  // Check user agent for mobile indicators (most reliable)
  const mobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Check for mobile-specific features
  const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
  const hasNoHover = window.matchMedia('(hover: none)').matches;
  const isSmallScreen = window.innerWidth <= 768 && window.innerHeight <= 900;
  
  // Only consider it mobile if:
  // 1. Mobile user agent detected, OR
  // 2. Has coarse pointer (touch-primary) AND no hover AND small screen
  return mobileUA || (hasCoarsePointer && hasNoHover && isSmallScreen);
}

export function showMobileLock() {
  const lockScreen = document.getElementById('mobile-lock-screen');
  if (lockScreen) {
    lockScreen.classList.add('visible');
  }
}

export function dismissMobileLock() {
  const lockScreen = document.getElementById('mobile-lock-screen');
  if (lockScreen) {
    lockScreen.dataset.dismissed = 'true';
    lockScreen.classList.remove('visible');
  }
  
  // Show mobile HUD for touch controls even without gyro
  const hud = document.getElementById('mobile-hud');
  if (hud && isMobileDevice()) {
    hud.classList.add('visible');
    // Hide calibrate button if no gyro
    const calibrateBtn = document.getElementById('calibrate-btn');
    if (calibrateBtn && !gyroControl.enabled) {
      calibrateBtn.style.display = 'none';
    }
    // Hide tilt indicator if no gyro
    const tiltIndicator = document.getElementById('tilt-indicator');
    if (tiltIndicator && !gyroControl.enabled) {
      tiltIndicator.style.display = 'none';
    }
    // Update status text
    updateGyroStatus(false, 'Touch controls only');
  }
}

// Enable gyro controls - called from lock screen button
export async function enableGyroControls() {
  // Check if DeviceOrientationEvent is available
  if (!window.DeviceOrientationEvent) {
    alert('Device orientation not supported on this device.');
    dismissMobileLock();
    return;
  }
  
  // iOS 13+ requires permission request (must be triggered by user gesture)
  if (typeof DeviceOrientationEvent.requestPermission === 'function') {
    try {
      const permission = await DeviceOrientationEvent.requestPermission();
      if (permission === 'granted') {
        startGyroListening();
        showMobileHUD();
        dismissMobileLock();
      } else {
        alert('Gyro permission denied. Using touch controls only.');
        dismissMobileLock();
      }
    } catch (error) {
      console.error('Gyro permission error:', error);
      alert('Could not enable gyro controls. Using touch controls only.');
      dismissMobileLock();
    }
  } else {
    // Android and older iOS - no permission needed
    startGyroListening();
    showMobileHUD();
    dismissMobileLock();
  }
}

// Start listening to device orientation events
export function startGyroListening() {
  window.addEventListener('deviceorientation', handleDeviceOrientation, true);
  
  // Enable gyro in the control state (will be picked up by main script)
  gyroControl.enabled = true;
  
  updateGyroStatus(true, 'Waiting for motion...');
}

// Handle device orientation events
export function handleDeviceOrientation(event) {
  // beta: front/back tilt (-180 to 180), positive = tilted forward
  // gamma: left/right tilt (-90 to 90), positive = tilted right
  const beta = event.beta;
  const gamma = event.gamma;
  
  // Skip if we don't have valid data (check for null/undefined, not falsy since 0 is valid)
  if (beta === null || beta === undefined || gamma === null || gamma === undefined) {
    updateGyroStatus(false, 'Waiting for gyro...');
    return;
  }
  
  // Store raw values for the main script to process
  gyroControl.rawBeta = beta;
  gyroControl.rawGamma = gamma;
  gyroControl.active = true;
  
  // Auto-calibrate on first valid reading (wait a moment for stable reading)
  if (!gyroControl.autoCalibrated && !gyroControl.calibrating) {
    gyroControl.calibrating = true;
    // Slight delay to let user settle their grip
    setTimeout(() => {
      gyroControl.neutralBeta = gyroControl.rawBeta;
      gyroControl.neutralGamma = gyroControl.rawGamma;
      gyroControl.autoCalibrated = true;
      gyroControl.calibrating = false;
      console.log('Gyro auto-calibrated:', { 
        beta: gyroControl.neutralBeta, 
        gamma: gyroControl.neutralGamma 
      });
    }, 500);
  }
  
  // Show raw values for debugging
  updateGyroStatus(true, `β:${beta.toFixed(0)}° γ:${gamma.toFixed(0)}°`);
}

// Show mobile HUD
export function showMobileHUD() {
  const hud = document.getElementById('mobile-hud');
  if (hud) {
    hud.classList.add('visible');
  }
  // Show tilt indicator immediately
  const tiltIndicator = document.getElementById('tilt-indicator');
  if (tiltIndicator) {
    tiltIndicator.classList.add('visible');
  }
}

// Hide mobile HUD
export function hideMobileHUD() {
  const hud = document.getElementById('mobile-hud');
  if (hud) {
    hud.classList.remove('visible');
  }
}

// Calibrate gyro - set current orientation as neutral
export function calibrateGyro() {
  gyroControl.needsCalibration = true;
  
  const btn = document.getElementById('calibrate-btn');
  if (btn) {
    btn.classList.add('calibrating');
    btn.textContent = 'Calibrated!';
    setTimeout(() => {
      btn.classList.remove('calibrating');
      btn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>
      </svg> Calibrate`;
    }, 1000);
  }
}

// Update gyro status indicator
export function updateGyroStatus(active, text) {
  const status = document.getElementById('gyro-status');
  const statusText = document.getElementById('gyro-status-text');
  if (status) {
    status.classList.toggle('active', active);
  }
  if (statusText) {
    statusText.textContent = text;
  }
}

// Update tilt indicator visual (mobile gyro feedback)
export function updateTiltIndicator(turnRate, climbRate) {
  const indicator = document.getElementById('tilt-indicator');
  const dot = document.getElementById('tilt-dot');
  
  if (!indicator || !dot) return;
  
  // Map -1 to 1 range to SVG coordinates (center is 50,50, range is ±35)
  const x = 50 + turnRate * 35;
  const y = 50 - climbRate * 35; // inverted Y for SVG
  
  dot.setAttribute('cx', x.toFixed(1));
  dot.setAttribute('cy', y.toFixed(1));
}

export function setupGyroControls() {
  // Check on page load
  if (isMobileDevice()) {
    showMobileLock();
  }
  
  // Also check on resize (in case orientation changes)
  let resizeTimeout;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(function() {
      const lockScreen = document.getElementById('mobile-lock-screen');
      // Only show if not already dismissed and is mobile
      if (lockScreen && !lockScreen.dataset.dismissed && isMobileDevice()) {
        showMobileLock();
      }
    }, 250);
  });
  
  // Setup lock screen button handlers
  const enableGyroBtn = document.getElementById('enable-gyro-btn');
  if (enableGyroBtn) {
    enableGyroBtn.addEventListener('click', enableGyroControls);
  }
  
  const dismissLockBtn = document.getElementById('dismiss-lock-btn');
  if (dismissLockBtn) {
    dismissLockBtn.addEventListener('click', dismissMobileLock);
  }
  
  // Setup calibrate button
  const calibrateBtn = document.getElementById('calibrate-btn');
  if (calibrateBtn) {
    calibrateBtn.addEventListener('click', calibrateGyro);
  }
}
